import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, trainStat, getStat, setFlag } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';

// The Fortress Library — a quiet annex off the great hall where the ghostly
// Librarian Crumb presides over "Crust, Toast & Jam," a rock-paper-scissors
// card game played with three loaves/spreads.

export function createFortressLibraryScene(){
  return () => new FortressLibraryScene();
}

const STONE   = 0x7C776C;
const STONE_D = 0x5E5A50;
const WOOD    = 0x5A3A22;
const GOLD    = 0xE8C24B;

// card types and the cyclic win rule
const CARDS = {
  crust: { name: 'Crust', icon: '🥖', beats: 'toast', color: '#B5824E' },
  toast: { name: 'Toast', icon: '🍞', beats: 'jam',  color: '#EFB96A' },
  jam:   { name: 'Jam',   icon: '🍯', beats: 'crust', color: '#E6537B' }
};
const CARD_KEYS = Object.keys(CARDS);

class FortressLibraryScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1A1420);
    this.scene.fog = new THREE.Fog(0x1A1420, 16, 46);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();
    this._buildShelves();
    this._buildTable();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 5.5);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.librarian = this._makeLibrarian();
    this.librarian.root.position.set(0, 0, -3.5);
    this.librarian.root.rotation.y = 0;
    this.scene.add(this.librarian.root);

    this.hotspots = [
      { pos: this.librarian.root.position.clone(), r: 2.6, label: '🗣 challenge Librarian Crumb', action: () => this._talkToLibrarian() },
      { pos: new THREE.Vector3(0, 0, 7.4), r: 2.2, label: '← back to the Fortress hall', action: () => this._exit() }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x4A3A48, 0.75));
    const moon = new THREE.DirectionalLight(0x7A8AB0, 0.55);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 18;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);

    // warm reading-lamp glow over the table
    const lamp = new THREE.PointLight(0xFF9A3C, 0.9, 14, 1.8);
    lamp.position.set(0, 4.5, -1.5); this.scene.add(lamp);
  }

  _buildRoom(){
    const HALF_W = 7, HALF_D = 8;
    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, HALF_D * 2), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // walls
    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    const wallGeo = new THREE.PlaneGeometry(HALF_D * 2, 8);
    const wallL = new THREE.Mesh(wallGeo, wallMat); wallL.rotation.y = Math.PI/2; wallL.position.set(-HALF_W, 4, 0); wallL.receiveShadow = true; this.scene.add(wallL);
    const wallR = new THREE.Mesh(wallGeo, wallMat); wallR.rotation.y = -Math.PI/2; wallR.position.set(HALF_W, 4, 0); wallR.receiveShadow = true; this.scene.add(wallR);
    const wallB = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, 8), wallMat); wallB.position.set(0, 4, -HALF_D); wallB.receiveShadow = true; this.scene.add(wallB);
    // back wall blocks exit except the arch to Fortress
    box(HALF_W * 2, 8, 0.4, STONE_D, 0, 4, HALF_D, this.scene);
    const archW = 2.6;
    box((HALF_W * 2 - archW) / 2, 8, 0.4, STONE_D, -(archW/2 + (HALF_W * 2 - archW)/4), 4, HALF_D, this.scene);
    box((HALF_W * 2 - archW) / 2, 8, 0.4, STONE_D,  (archW/2 + (HALF_W * 2 - archW)/4), 4, HALF_D, this.scene);
    box(archW + 0.2, 2.4, 0.5, STONE, 0, 6.6, HALF_D, this.scene);
    // ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, HALF_D * 2), new THREE.MeshLambertMaterial({ color: 0x1A1218 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 8, 0); this.scene.add(ceil);
    // sign
    const sign = textSprite('The Crust Archives', 28, '#EFE6D0', 4.2, 0.85);
    sign.position.set(0, 6.2, -HALF_D + 0.4); this.scene.add(sign);
    const exitSign = textSprite('← to Fortress', 22, '#C9B98F', 2.6, 0.7);
    exitSign.position.set(0, 4.4, HALF_D - 0.3); this.scene.add(exitSign);
  }

  _buildShelves(){
    // tall bookcases along the side walls
    [-6.2, 6.2].forEach(x => {
      for (let z = -6; z <= 4; z += 2.4){
        const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
        box(0.6, 5.0, 1.8, WOOD, 0, 2.5, 0, g); // case
        // rows of books
        for (let row = 0; row < 4; row++){
          const y = 0.8 + row * 1.15;
          for (let b = 0; b < 5; b++){
            const col = 0x6B4A2F + ((b + row) % 4) * 0x102030;
            const bk = box(0.12, 0.7, 1.4, col, -0.18 + b * 0.18, y, 0, g);
            bk.rotation.z = (Math.random() - 0.5) * 0.08;
          }
        }
      }
    });
    // rolling ladder
    const ladder = new THREE.Group(); ladder.position.set(-4.8, 0, 1.2); this.scene.add(ladder);
    cyl(0.04, 0.04, 5.2, 0x444444, -0.35, 2.6, 0, ladder, 6);
    cyl(0.04, 0.04, 5.2, 0x444444, 0.35, 2.6, 0, ladder, 6);
    for (let i = 0; i < 6; i++) cyl(0.03, 0.03, 0.74, 0x333333, 0, 0.5 + i * 0.9, 0, ladder, 6).rotation.z = Math.PI/2;
  }

  _buildTable(){
    // big reading table in the center
    box(3.6, 0.14, 2.0, WOOD, 0, 1.0, -1.5, this.scene);
    cyl(0.12, 0.14, 1.0, 0x3E2A1A, -1.4, 0.5, -2.2, this.scene, 8);
    cyl(0.12, 0.14, 1.0, 0x3E2A1A,  1.4, 0.5, -2.2, this.scene, 8);
    cyl(0.12, 0.14, 1.0, 0x3E2A1A, -1.4, 0.5, -0.8, this.scene, 8);
    cyl(0.12, 0.14, 1.0, 0x3E2A1A,  1.4, 0.5, -0.8, this.scene, 8);
    // scattered cards on the table
    [-0.6, 0, 0.6].forEach(x => {
      const card = box(0.35, 0.02, 0.5, C.cream, x, 1.08, -1.5, this.scene);
      card.rotation.y = (Math.random() - 0.5) * 0.3;
    });
  }

  _makeLibrarian(){
    const GHOST = 0xA0C8D8;
    const build = (g) => {
      // translucent-ish pale loaf in a robe
      const body = sphere(0.34, GHOST, 0, 0.95, 0, g, 14);
      body.material.transparent = true; body.material.opacity = 0.82;
      box(0.72, 0.9, 0.42, GHOST, 0, 0.55, 0, g).material.transparent = true;
      sphere(0.07, 0x2E2018, -0.14, 1.1, 0.24, g, 8);
      sphere(0.07, 0x2E2018,  0.14, 1.1, 0.24, g, 8);
      // spectacles
      cyl(0.04, 0.04, 0.06, 0x333333, -0.14, 1.05, 0.3, g, 8).rotation.x = Math.PI/2;
      cyl(0.04, 0.04, 0.06, 0x333333,  0.14, 1.05, 0.3, g, 8).rotation.x = Math.PI/2;
      // book
      box(0.35, 0.08, 0.5, C.cream, 0.45, 0.65, 0.2, g).rotation.y = 0.4;
    };
    return new Townsperson({
      name: 'Librarian Crumb', line: 'shhh… pick a card. crust beats toast, toast beats jam, jam beats crust.',
      pos: new THREE.Vector3(0, 0, -3.5), facing: 0, wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this.bread.setLantern(hasFound('ghost-lamp') ? 1.0 : 0);
    this._toExplore(true);
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input?.azimuthGoal ?? 0;
    this.polar   = this.input?.polarGoal ?? 0.5;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the crust archives');
    if (first) HUD.say('Dusty shelves and soft lamplight. Librarian Crumb floats behind the reading table, cards in hand.');
    HUD.setHint('click to walk · talk to Librarian Crumb to play Crust, Toast & Jam · or head back');
    HUD.hideHintSoon(9000);
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.librarian, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-7, Math.min(7, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _talkToLibrarian(){
    this.bread.stop();
    this.bread.drawFace('grin');
    this._startMatch();
  }

  _exit(){
    this.manager.goTo('fortress', { from: 'fortressLibrary' });
  }

  // ---- card game ----
  _startMatch(){
    this.state = 'playing';
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');
    this.match = { playerScore: 0, foeScore: 0, round: 1, done: false };
    this._resolving = false;
    this._duel = null;
    this._renderGamePanel();
  }

  _renderGamePanel(resultText = ''){
    this._clearPanels();
    const m = this.match;
    const duel = this._duel || {};
    const duelHtml = duel.playerCard ? `
      <div class="card-duel">
        <div class="card-slot ${duel.result === 'win' ? 'winner' : duel.result === 'lose' ? 'loser' : ''}">
          <span class="card-icon">${CARDS[duel.playerCard].icon}</span>
          <span class="card-name">You</span>
        </div>
        <div class="card-vs">VS</div>
        <div class="card-slot foe ${duel.result === 'lose' ? 'winner' : duel.result === 'win' ? 'loser' : ''}">
          <span class="card-icon">${duel.result ? CARDS[duel.foeCard].icon : '❓'}</span>
          <span class="card-name">Crumb</span>
        </div>
      </div>
      <div class="card-result-line ${duel.result || ''}">
        ${duel.result === 'win' ? 'You win the round!' : duel.result === 'lose' ? 'Crumb wins the round.' : duel.result === 'tie' ? 'Tie — replay the round!' : ''}
      </div>` : '';
    const p = this._panel(`
      <div class="cafe-card card-game">
        <h2>Crust, Toast & Jam</h2>
        <div class="cafe-sub">Round ${m.round} · first to 2 wins</div>
        <div class="card-score">
          <span>you <b>${m.playerScore}</b></span>
          <span>crumb <b>${m.foeScore}</b></span>
        </div>
        ${duelHtml}
        ${resultText ? `<div class="card-result">${resultText}</div>` : ''}
        <div class="card-hand ${this._resolving ? 'disabled' : ''}">
          ${CARD_KEYS.map(k => {
            const c = CARDS[k];
            return `<button class="card-btn" data-card="${k}" style="--card:${c.color}" ${this._resolving ? 'disabled' : ''}>
              <span class="card-icon">${c.icon}</span>
              <span class="card-name">${c.name}</span>
              <span class="card-beats">beats ${CARDS[c.beats].name}</span>
            </button>`;
          }).join('')}
        </div>
        <button class="cafe-btn ghost card-leave" ${this._resolving ? 'disabled' : ''}>leave table</button>
      </div>
    `);
    if (!this._resolving){
      p.querySelectorAll('.card-btn').forEach(b => b.onclick = () => this._playRound(b.dataset.card));
      p.querySelector('.card-leave').onclick = () => this._endMatch(true);
    }
  }

  _playRound(playerCard){
    if (this.match.done || this._resolving) return;
    const foeCard = CARD_KEYS[(Math.random() * CARD_KEYS.length) | 0];
    const result = this._resolve(playerCard, foeCard);
    let resultText = `You played ${CARDS[playerCard].icon} ${CARDS[playerCard].name} · Crumb played ${CARDS[foeCard].icon} ${CARDS[foeCard].name}<br>`;

    if (result === 'win'){
      this.match.playerScore++;
      resultText += '<b>You win the round!</b>';
      this.bread.drawFace('grin');
    } else if (result === 'lose'){
      this.match.foeScore++;
      resultText += '<b>Crumb wins the round.</b>';
      this.bread.drawFace('o');
    } else {
      resultText += '<b>Tie — replay the round.</b>';
      this.bread.drawFace('happy');
    }

    this._duel = { playerCard, foeCard, result };
    this._resolving = true;
    this._renderGamePanel();

    if (this.match.playerScore >= 2 || this.match.foeScore >= 2){
      this._finishMatch(resultText);
    } else {
      if (result !== 'tie') this.match.round++;
      setTimeout(() => { this._resolving = false; this._duel = null; this._renderGamePanel(resultText); }, 1100);
    }
  }

  _resolve(a, b){
    if (a === b) return 'tie';
    return CARDS[a].beats === b ? 'win' : 'lose';
  }

  _finishMatch(lastRoundText){
    this.match.done = true;
    const won = this.match.playerScore >= 2;
    const crumbs = won ? 12 + getStat('int') * 2 : 0;
    if (won){
      addCrumbs(crumbs); HUD.refreshCrumbs();
      const intGain = trainStat('int', 1);
      if (intGain > 0) HUD.refreshStats();
      setFlag('libraryMatchWon', true);
      const firstBook = !hasFound('lore-book');
      addFound('lore-book');
      this.bread.drawFace('yum');

      this._clearPanels();
      const p = this._panel(`
        <div class="cafe-card card-game">
          <div class="cafe-grade">🏆</div>
          <h2>Victory!</h2>
          <div class="cafe-sub">best of 3 rounds</div>
          <div class="card-result">${lastRoundText}</div>
          <p class="fortress-loot">📜 Baker King's Ledger</p>
          <p class="fortress-note">${firstBook ? 'a crumbly page of royal lore — added to your collection!' : 'another copy of the ledger. still dusty.'}</p>
          <p class="cafe-payout">🍞 +${crumbs} crumbs${intGain > 0 ? ` · +${intGain} INT` : ''}</p>
          <button class="cafe-btn card-again">play again</button>
          <button class="cafe-btn ghost card-leave">leave</button>
        </div>
      `);
      p.querySelector('.card-again').onclick = () => this._startMatch();
      p.querySelector('.card-leave').onclick = () => this._endMatch(true);
    } else {
      this.bread.drawFace('effort');
      this._clearPanels();
      const p = this._panel(`
        <div class="cafe-card card-game">
          <div class="cafe-grade">💨</div>
          <h2>Crumb wins this match</h2>
          <div class="cafe-sub">best of 3 rounds</div>
          <div class="card-result">${lastRoundText}</div>
          <p class="cafe-payout">no crumbs this time, but you learned their tells.</p>
          <button class="cafe-btn card-again">try again</button>
          <button class="cafe-btn ghost card-leave">leave</button>
        </div>
      `);
      p.querySelector('.card-again').onclick = () => this._startMatch();
      p.querySelector('.card-leave').onclick = () => this._endMatch(true);
    }
  }

  _endMatch(leave){
    this._clearPanels();
    this.state = 'explore';
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.bread.resetLimbs();
    if (leave){
      HUD.say('Librarian Crumb shuffles the deck and fades back into the shelves.');
      this.bread.drawFace('happy');
    }
  }

  // ---- panels ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- loop ----
  update(dt){
    this.librarian.update(dt, this.bread.root.position);
    this.bread.update(dt);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
